import {
  BarChart3, FileCheck, Inbox, LayoutGrid,
  Network, PenLine, PiggyBank, Receipt, UserCheck, UserCog, Wallet,
  type LucideIcon,
} from '../icons';

/**
 * 전자결재 메뉴 트리 — 「전자결재」 셸(`ApprShell`)의 좌측 칸이 그리는 목록 한 벌.
 *
 * ── 왜 이 파일이 생겼나 (2026-08-31 · 사용자 지적 「전자결재 메뉴 양식이 다 틀리다」) ──
 * 결재만 **양식이 달랐다.** 인사(`hr-menu`)·근태(`att-menu`)는 트리 한 벌을 이 자리에 두고
 * 셸이 `<Outlet/>` 으로 화면을 갈아 끼우는데, 결재는 **화면 안에 220px 사이드바를 들고**
 * 제 상태로만 움직였다. 그래서
 *   · 주소가 하나뿐이라(`/approval`) **어느 함도 북마크할 수 없었고**,
 *   · 트리에 **머리(제목·검색)가 없어** 옆 메뉴들과 생김새가 달랐으며,
 *   · 이름이 「승인대기함 · 승인처리함 · 기안문서함」처럼 시안과 어긋나 있었다.
 *
 * ── 시안 ─────────────────────────────────────────────────────────────────
 * `docs/hr-attendance-approval-redesign-sample.html` F1·F2·F3 이 같은 트리를 세 번 그린다:
 *   결재하기 — 결재 대기 · 기안 문서함 · 참조 · 열람
 *   관리 · 담당자 — 결재 현황 · 양식 · 서식 · 결재선 · 위임 · 계정과목
 *   개인 설정 — 서명 · 도장
 * ★ 「결재 홈」은 시안 그림에는 없지만 **같은 시안이 F5 에서 요구한다**(「들어오면 곧장
 *   승인대기함」이 지금 상태이고, 착지 화면을 두자는 것). 그래서 결재하기의 첫 줄로 둔다.
 *
 * ── 규율: 트리에는 이 셸 안에 사는 것만 ──────────────────────────────────
 * 근태 판 A 의 규율을 그대로 따른다 — `to` 는 전부 `/approval/*` 다. 서명·도장만 창(모달)이라
 * 주소가 없고, 그것은 `action` 으로 표시한다(`appr-menu.test.ts` 가 못 박는다).
 */
export interface ApprMenuItem {
  /** 주소. 창을 여는 항목(서명·도장)은 없다. */
  to?: string;
  label: string;
  /** 항목 아래 한 줄 — 「이 화면이 무엇을 하는가」. */
  hint: string;
  icon: LucideIcon;
  /** 주소가 아니라 **창**을 여는 항목. 지금은 서명 하나뿐이다. */
  action?: 'signature';
}

export interface ApprMenuGroup {
  label: string;
  items: ApprMenuItem[];
}

/** 결재 셸의 뿌리 경로. 라우터·레일·트리가 같은 값을 본다. */
export const APPR_HOME_PATH = '/approval';

/**
 * 전 직원이 보는 묶음.
 *
 * ★ 「승인처리함」(내가 올려서 끝난 것)은 **세우지 않는다** — 「기안 문서함」이 내가 올린 것을
 *   **전부** 담아 그 부분집합일 뿐이다(서버 `completed` 는 `drafted` 의 status 부분집합).
 *   시안이 그 함을 안 그린 이유도 같다. 값은 서버에 그대로 살아 있다(회귀 0).
 * ★ 「참조 · 열람」은 **이 판에서 처음 생긴다** — 참조자 줄은 migration 120 부터 쌓였는데
 *   그것을 여는 함이 없어, 참조를 걸어도 받는 쪽에는 아무 자리에도 안 떴다.
 */
const WORK_GROUP: ApprMenuGroup = {
  label: '결재하기',
  items: [
    { to: APPR_HOME_PATH, label: '결재 홈', hint: '지금 할 일 · 내가 올린 것', icon: LayoutGrid },
    /**
     * ★★ **결재함** (2026-09-13 · 과제 L · 사용자 확인 시안 axi-approval-status v2) — 결재 대기 · 진행 · 완료 · 반려 ·
     *    임시 · 올린 문서 · 참고를 **한 화면 트리**로 모은 목록. 행을 누르면 문서 화면(`/approval/doc/:id`)으로 간다.
     * ★★ 옛 문서함 넷(결재 대기 · 기안 문서함 · 임시 · 참조 · 열람)은 2026-09-13(L2)에 이 화면의 **마디**로 들어왔다 —
     *    트리에서 뺐고, 옛 주소는 `/approval/box?box=` 로 넘긴다(router · `LEGACY_BOX_REDIRECTS`).
     */
    { to: '/approval/box', label: '결재함', hint: '받은 · 올린 · 참고 문서를 한 곳에서', icon: Inbox },
    /**
     * ★★ **임시** (2026-09-04 · 판 P2) — 「기안 문서함」과 다르다.
     *    저것은 «내가 올린 것 전부», 이것은 **«아직 안 올린 것»**이다.
     * ★★ 2026-09-13(L2) — 결재함의 「임시」 마디(`/approval/box?box=temp`)로 들어가 트리에서는 빠졌다.
     */
    /**
     * ★★ **내 지출결의** (2026-09-04 · 예산 판 B1) — 지출결의서 결재의 «뒤».
     *    종전에는 승인돼도 남는 자리가 없어 문서 한 줄로 끝났다(파이프라인 선언이 그 자리를
     *    `MISSING` 으로 적어 두고 있었다). 이제 줄마다 행이 되고 여기서 그 결과를 본다.
     */
    { to: '/approval/expense', label: '내 지출결의', hint: '올린 지출이 어떻게 됐나', icon: Receipt },
  ],
};

/**
 * 담당자(회사 관리자)만 보는 묶음 — **서버가 판정하는 것과 같은 선**이다.
 *
 * ★★ 결재 현황 · 양식 · 서식 · 계정과목은 서버가 `ADMIN`·`SYSTEM_ADMIN` 이 아니면 403 이다.
 *    **할 수 없는 일을 메뉴에 세우면 그게 잘못된 안내다**(인사 메뉴에서 배운 것).
 * ★ 「위임」은 여기서 **회사 전체**를 본다(`scope=company`) — 내가 맡기는 것은 「개인 설정」의
 *   「내 위임」이다. 주인이 다르면 자리도 이름도 다르다.
 */
const ADMIN_GROUP: ApprMenuGroup = {
  label: '관리 · 담당자',
  items: [
    { to: '/approval/admin/stats', label: '결재 현황', hint: '양식별 · 사람별 병목', icon: BarChart3 },
    { to: '/approval/admin/forms', label: '양식 · 서식', hint: '종류 · 칸 · 전결', icon: FileCheck },
    /**
     * 결재함 (2026-09-07 · 시안 A1) — 「결재양식」 창의 **왼쪽 묶음**이자 전사결재함.
     * ★ 일곱은 씨앗이고 표는 비어 있는 것이 정상이다 — 회사가 손댈 때 행이 생긴다.
     */
    // ★ 2026-09-13(과제 L · 사용자 결정) — 문서를 여는 「결재함」(/approval/box)과 이름이 겹쳐 「결재함 설정」 으로 바꿨다.
    { to: '/approval/admin/boxes', label: '결재함 설정', hint: '양식의 묶음 · 전사 함', icon: Inbox },
    /**
     * ⚠ **부제에서 「사다리」를 걷어냈다** (2026-09-04 · 판 P6).
     *    판 P1 이 조직 사다리를 **폐기**했는데 이 한 줄이 남아 있었다 — 화면에는 사다리가
     *    없는데 메뉴는 있다고 말하는 상태였다. **판 P0 이 잡아낸 것과 같은 부류**의
     *    잔재이고, 그때처럼 「고친 절」이 아니라 **머리의 한 줄**이 남았다.
     */
    { to: '/approval/admin/lines', label: '결재선', hint: '기본 결재선 · 즐겨찾기', icon: Network },
    { to: '/approval/admin/delegates', label: '위임', hint: '회사 전체 · 기간', icon: UserCog },
    { to: '/approval/admin/codes', label: '계정과목', hint: '지출결의서가 고른다', icon: Wallet },
    /**
     * ★ **회사 전체 지출** — 「내 지출결의」와 **주소가 다르다**. 남의 지출 금액이 통째로
     *   나가는 자리라 서버가 관리자만 통과시킨다(할 수 없는 일을 메뉴에 세우지 않는다).
     */
    { to: '/approval/admin/expense', label: '지출 집행', hint: '계정과목별 · 회사 전체', icon: Receipt },
    /**
     * ★★ **예산** (2026-09-04 · 예산 판 B2) — 계정과목 · 지출 집행 옆이 제자리다.
     *    편성 − 집행 = 잔액이고, **신청중을 빼야 가용**이다.
     */
    { to: '/approval/admin/budget', label: '예산', hint: '편성 · 잔액 · 가용', icon: PiggyBank },
  ],
};

/**
 * 누구나 보는 개인 묶음.
 *
 * ★ 「서명 · 도장」은 **프로필의 「내 서명」과 같은 창**을 연다 — 두 벌로 만들면 한쪽만
 *   고쳐지는 날이 온다. 그래서 주소가 없고 `action` 이다.
 */
const PERSONAL_GROUP: ApprMenuGroup = {
  label: '개인 설정',
  items: [
    { to: '/approval/me/delegates', label: '내 위임', hint: '내가 맡기는 것 · 기간', icon: UserCheck },
    { label: '서명 · 도장', hint: '결재에 찍히는 내 도장', icon: PenLine, action: 'signature' },
  ],
};

export const APPR_MENU: readonly ApprMenuGroup[] = [WORK_GROUP, ADMIN_GROUP, PERSONAL_GROUP];
export const MY_APPR_MENU: readonly ApprMenuGroup[] = [WORK_GROUP, PERSONAL_GROUP];

/**
 * 이 사람이 볼 트리.
 *
 * ★ 기본값이 거짓인 것은 **모르면 안 보여 주는 쪽**이 맞기 때문이다(근태·인사와 같은 규칙).
 */
export function apprMenuFor(isAdmin: boolean): readonly ApprMenuGroup[] {
  return isAdmin ? APPR_MENU : MY_APPR_MENU;
}

/**
 * 트리 검색 — **목록만 거른다.** 치는 도중에 오른쪽 화면이 발밑에서 바뀌면 안 된다
 * (인사·근태 트리와 같은 규칙). 이름과 부제를 함께 본다.
 */
export function filterApprMenu(groups: readonly ApprMenuGroup[], query: string): ApprMenuGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups.map((g) => ({ ...g, items: [...g.items] }));
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (i) => i.label.toLowerCase().includes(q) || i.hint.toLowerCase().includes(q),
      ),
    }))
    .filter((g) => g.items.length > 0);
}
